/**
 * App Component
 *
 * Main application component with routing
 */

import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Layout } from '@/components/layout/Layout';
import { NewMissionPage } from '@/pages/NewMissionPage';
import { MissionHistoryPage } from '@/pages/MissionHistoryPage';
import { SettingsPage } from '@/pages/SettingsPage';
import { LiveOperation } from '@/pages/LiveOperation';
import { Toaster } from 'sonner';
import '@/styles/cesium-overrides.css';
import { GearPage } from '@/pages/GearPage';
import { AreaInterestPage } from '@/pages/AreaInterestPage';
import { AreaInterestForm } from '@/pages/AreaInterestForm';
import { NewFlightPage } from '@/pages/NewFlightPage';
import { DashboardPage } from '@/pages/DashboardPage';
import { LoginPage } from '@/pages/LoginPage';

import { ProtectedRoute } from '@/features/auth/components/ProtectedRoute';
import { AuthProvider } from '@/features/auth/components/AuthProvider';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter
        future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
      >
        <AuthProvider>
          <Routes>
            {/* Auth Route - No Layout */}
            <Route path="/login" element={<LoginPage />} />

            {/* Main App Routes - With Layout & Protection */}
            <Route
              path="/*"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Routes>
                      {/* Default route - redirect to dashboard */}
                      <Route
                        path="/"
                        element={<Navigate to="/dashboard" replace />}
                      />

                      {/* Dashboard */}
                      <Route path="/dashboard" element={<DashboardPage />} />

                      {/* Mission Planning */}
                      <Route path="/mission" element={<NewMissionPage />} />

                      {/* Mission History */}
                      <Route
                        path="/mission-history"
                        element={<MissionHistoryPage />}
                      />

                      {/* Live Operations */}
                      <Route
                        path="/live-operation"
                        element={<LiveOperation />}
                      />

                      {/* Asset Management */}
                      <Route
                        path="/asset-management/gear"
                        element={<GearPage />}
                      />

                      {/* Mission Planning */}
                      <Route path="/new-flight" element={<NewFlightPage />} />
                      <Route path="/mission-planning/area-of-interest" element={<AreaInterestPage />} />
                      <Route path="/mission-planning/area-of-interest/form" element={<AreaInterestForm />} />
                      <Route path="/mission-planning/area-of-interest/form/:id" element={<AreaInterestForm />} />

                      {/* Settings */}
                      <Route path="/settings" element={<SettingsPage />} />
                    </Routes>
                  </Layout>
                </ProtectedRoute>
              }
            />
          </Routes>
          <Toaster position="top-right" theme="dark" richColors />
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
